import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import HowToSteps, { HowToStep } from '@comp/learn/how-to-steps'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

interface PageContent {
  metaTitle: string
  metaDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  badges: string[]
  primaryCta: string
  secondaryCta: string
  stepsEyebrow: string
  stepsTitle: string
  stepsBody: string
  docLabel: string
  steps: HowToStep[]
  relatedEyebrow: string
  relatedTitle: string
  related: RelatedItem[]
}

const content: Record<'en' | 'tr', PageContent> = {
  en: {
    metaTitle: 'Silent Kerberos SSO for domain-joined clients',
    metaDescription:
      'Set up SPNEGO sign-in with Monosign: SPN and keytab, network scoping so off-domain browsers never see a credential popup, and silent fallback to the standard form. Works across multiple AD forests.',
    heroEyebrow: 'How-to · Kerberos SSO',
    heroTitle: ['Zero-click sign-in.', 'For every domain-joined desktop.'],
    heroBody:
      'On a domain-joined workstation, the user already authenticated when they unlocked the machine. Kerberos sign-in turns that into silent web SSO: the browser presents a ticket, Monosign validates it, and the sign-in form never appears. Configured per AD source, scoped by client network, with silent fallback everywhere else.',
    badges: ['SPNEGO / Negotiate', 'Multiple AD forests', 'Keytab or SPN + password'],
    primaryCta: 'Start free trial →',
    secondaryCta: 'Open the docs',
    stepsEyebrow: 'Walkthrough',
    stepsTitle: 'From SPN to silent sign-in.',
    stepsBody: 'Four steps on the AD source. Failure is always silent: the form stays.',
    docLabel: 'Continue in the full docs',
    steps: [
      {
        title: 'Register the SPN and create the credential',
        body: 'Map an HTTP/<sso-host> service principal name to a dedicated AD service account and export a keytab with ktpass, or skip the keytab and use SPN + service account name + password mode. When both are configured, the keytab wins.',
        hint: 'The realm goes in uppercase, and the keytab is stored encrypted in the secret store; it never leaves the API tier.',
      },
      {
        title: 'Enable Kerberos on the AD source in Monosign',
        body: 'On the Active Directory user source, switch Kerberos on and enter the realm plus the keytab (or the SPN/account/password trio). Each source carries its own configuration, so several forests can run Kerberos side by side: tickets resolve to the right source by realm, and users are looked up in that source only.',
      },
      {
        title: 'Scope the silent attempt by client network',
        body: 'List the IP ranges (exact IPs or CIDR) where domain-joined machines actually live. Clients outside these networks are never offered the Negotiate challenge, which is exactly what prevents the raw browser credential popup on home laptops and phones.',
        hint: 'Empty means all clients; unresolvable addresses fail closed. Scope per source, so each forest can declare its own networks.',
      },
      {
        title: 'Configure browsers and test the fallback',
        body: 'Add the SSO host to the browsers’ trusted-for-SPNEGO list via group policy (Chrome, Edge, and Firefox each have a policy for it), then test three cases: a domain machine signs in with zero clicks, an off-network machine sees the normal form with no popup, and a domain machine with no matching user silently falls back to the form.',
        hint: 'Kerberos is an optimization, never a gate: every failure path must end at the standard sign-in form, invisibly.',
      },
    ],
    relatedEyebrow: 'Keep exploring',
    relatedTitle: 'Related Monofor capabilities.',
    related: [
      { label: 'Single Sign-On', description: 'The SSO capability Kerberos plugs into.', href: '/sign/sso' },
      { label: 'Sync Active Directory', description: 'Connect AD as a Monosign identity source first.', href: '/learn/active-directory-sync' },
      { label: 'Migrate from ADFS', description: 'Retire the farm app by app; AD stays the source of truth.', href: '/learn/migrate-from-adfs' },
    ],
  },
  tr: {
    metaTitle: "Domain'e bağlı istemciler için sessiz Kerberos SSO",
    metaDescription:
      "Monosign ile SPNEGO girişini kurun: SPN ve keytab, domain dışı tarayıcıların kimlik popup'ı görmemesi için ağ kapsamı ve standart forma sessiz geri düşüş. Birden çok AD ormanında çalışır.",
    heroEyebrow: 'Rehber · Kerberos SSO',
    heroTitle: ['Sıfır tık giriş.', "Domain'e bağlı her masaüstünde."],
    heroBody:
      "Domain'e bağlı iş istasyonunda kullanıcı, makinenin kilidini açarken zaten doğrulandı. Kerberos girişi bunu sessiz web SSO'ya çevirir: tarayıcı bileti sunar, Monosign doğrular ve giriş formu hiç görünmez. AD kaynağı başına yapılandırılır, istemci ağına göre kapsanır, diğer her durumda sessizce forma düşer.",
    badges: ['SPNEGO / Negotiate', 'Birden çok AD ormanı', 'Keytab veya SPN + parola'],
    primaryCta: 'Ücretsiz deneme başlat →',
    secondaryCta: 'Dokümantasyonu açın',
    stepsEyebrow: 'Adım adım',
    stepsTitle: "SPN'den sessiz girişe.",
    stepsBody: 'AD kaynağında dört adım. Başarısızlık hep sessizdir: form yerinde kalır.',
    docLabel: 'Rehberin tamamına gidin',
    steps: [
      {
        title: "SPN'i kaydedin ve kimlik bilgisini oluşturun",
        body: "HTTP/<sso-host> servis asıl adını özel bir AD servis hesabına eşleyin ve ktpass ile keytab üretin, ya da keytab'ı atlayıp SPN + servis hesabı adı + parola modunu kullanın. İkisi birden tanımlıysa keytab kazanır.",
        hint: "Realm büyük harfle yazılır; keytab secret store'da şifreli saklanır; API katmanının dışına hiç çıkmaz.",
      },
      {
        title: "Monosign'da AD kaynağında Kerberos'u açın",
        body: "Active Directory kullanıcı kaynağında Kerberos'u etkinleştirin; realm ile keytab'ı (veya SPN/hesap/parola üçlüsünü) girin. Her kaynak kendi yapılandırmasını taşır; birden çok orman yan yana Kerberos çalıştırabilir: biletler realm üzerinden doğru kaynağa çözülür ve kullanıcı yalnızca o kaynakta aranır.",
      },
      {
        title: 'Sessiz denemeyi istemci ağına göre kapsayın',
        body: "Domain'e bağlı makinelerin gerçekten yaşadığı IP aralıklarını (tek IP ya da CIDR) listeleyin. Bu ağların dışındaki istemcilere Negotiate challenge'ı hiç önerilmez; ev dizüstülerinde ve telefonlarda çıplak tarayıcı kimlik popup'ını engelleyen tam olarak budur.",
        hint: 'Boş bırakmak tüm istemciler demektir; çözülemeyen adresler güvenli tarafta kalır (fail-closed). Kapsam kaynak başınadır; her orman kendi ağını bildirebilir.',
      },
      {
        title: "Tarayıcıları yapılandırın ve geri düşüşü test edin",
        body: "SSO host'unu grup politikasıyla tarayıcıların SPNEGO güvenilenler listesine ekleyin (Chrome, Edge ve Firefox'un her birinin politikası var) ve üç durumu test edin: domain makinesi sıfır tıkla girer, ağ dışı makine popup'sız normal formu görür, eşleşen kullanıcısı olmayan domain makinesi sessizce forma düşer.",
        hint: 'Kerberos bir optimizasyondur, asla kapı değildir: her başarısızlık yolu görünmez biçimde standart giriş formunda bitmelidir.',
      },
    ],
    relatedEyebrow: 'Keşfetmeye devam edin',
    relatedTitle: 'İlgili Monofor yetenekleri.',
    related: [
      { label: 'Tek Oturum Açma (SSO)', description: "Kerberos'un bağlandığı SSO yeteneği.", href: '/sign/sso' },
      { label: 'Active Directory senkronizasyonu', description: "Önce AD'yi Monosign kimlik kaynağı olarak bağlayın.", href: '/learn/active-directory-sync' },
      { label: "ADFS'ten geçiş", description: 'Çiftliği uygulama uygulama emekliye ayırın; AD doğruluk kaynağı kalır.', href: '/learn/migrate-from-adfs' },
    ],
  },
}

type Props = { locale: string }

export default function KerberosSsoSetupPage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <Layout>
      <SolutionMeta
        title={c.metaTitle}
        description={c.metaDescription}
        path="/learn/kerberos-sso-setup"
        locale={locale}
        bilingual
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow={c.heroEyebrow}
          title={
            <>
              {c.heroTitle[0]}
              <br />
              {c.heroTitle[1]}
            </>
          }
          body={c.heroBody}
          badges={c.badges}
          primaryCta={{ label: c.primaryCta, href: '/try/now' }}
          secondaryCta={{ label: c.secondaryCta, href: 'https://help.monofor.com/monosign/kerberos-authentication' }}
        />
        <HowToSteps
          eyebrow={c.stepsEyebrow}
          title={<>{c.stepsTitle}</>}
          body={c.stepsBody}
          steps={c.steps}
          schemaName={c.metaTitle}
          docHref="https://help.monofor.com/monosign/kerberos-authentication"
          docLabel={c.docLabel}
        />
        <SolutionRelated
          eyebrow={c.relatedEyebrow}
          title={c.relatedTitle}
          items={c.related}
        />
        <FinalCta />
      </main>
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
